// #region Imports
import Modal from 'react-bootstrap/Modal';
import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import '../../../scss/Components/_modal.scss';
import 'react-toastify/dist/ReactToastify.css';
import customAxios from '../../../utils/customAxios';
import { GlobalVariables } from '../../../globalVar';
import { getToast } from '../../../utils/toastConfig';
import { useTranslationHook } from '../../../hooks/UseTranslationHook';
// #endregion

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const ModalA = ({ show, onHide, getAssets }) => {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState(null);
  const [clients, setClients] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [locationId, setLocationId] = useState(null);
  const [locations, setLocations] = useState([]);

  const { notesString } = useTranslationHook();

  useEffect(() => {
    if (show) fetchData();
  }, [show]);

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [clientsData, locationsData] = await Promise.all([
        fetchClients(),
        fetchLocations(),
      ]);

      setClients(clientsData);
      setLocations(locationsData);
    } catch (error) {
      console.log('Error loading asset modal data:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const fetchLocations = async () => {
    const endpoint = `${GlobalVariables.getURL()}locations-assets/`;
    const response = await customAxios.get(endpoint, HEADERS);

    if (response.status === 204) return [];
    return response.data?.data || response.data || [];
  };

  const fetchClients = async () => {
    const endpoint = `${GlobalVariables.getURL()}autofillVisits`;
    const response = await customAxios.get(endpoint, HEADERS);

    if (response.status === 204) return [];
    return response.data?.clients || [];
  };

  useEffect(() => {
    if (clientId) {
      fetchSites();
    } else {
      setSiteId(null);
      setSites([]);
    }
  }, [clientId]);

  const fetchSites = async () => {
    try {
      const endpoint = `${GlobalVariables.getURL()}listClientSites/${clientId}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setSites([]);
        return;
      }

      setSites(response.data || []);
    } catch (error) {
      console.log('Error loading sites:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    }
  };

  const selectedSite = sites.find((s) => s.id === siteId);

  const resetFields = () => {
    setName('');
    setClientId(null);
    setClients([]);
    setSiteId(null);
    setSites([]);
    setNotes('');
    setLocationId(null);
    setLocations([]);
  };

  const hide = () => {
    onHide();
    getToast({
      status: 'success',
      title: 'Equipo creado exitosamente en la base de datos.',
    });
    resetFields();
    getAssets();
  };

  const createAsset = async (event) => {
    setIsLoading(true);
    event.preventDefault();

    try {
      const endpoint = `${GlobalVariables.getURL()}createAsset`;
      const assetData = {
        name,
        description: notes,
        notes,
        site_id: siteId,
        location_id: locationId,
      };

      const response = await customAxios.post(endpoint, assetData, HEADERS);

      if (response.status === 200) hide();
    } catch (error) {
      console.log('Error creating asset:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExiting={resetFields}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName="modal-backdrop"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Nuevo equipo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="new-asset-form" onSubmit={createAsset}>
          <div className="row">
            <div className="col-md-12">
              <TextField
                disabled={isLoading}
                fullWidth
                label="Nombre"
                placeholder="Nombre..."
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>

          <br />

          <div className="row">
            <div className="col-md-6">
              {isFetching ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <Autocomplete
                  disabled={isLoading}
                  options={clients}
                  value={clients.find((c) => c.id === clientId) || null}
                  getOptionLabel={(c) => c.business_name || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_, newValue) =>
                    setClientId(newValue ? newValue.id : null)
                  }
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      label="Plaza"
                      placeholder="Plaza..."
                    />
                  )}
                />
              )}
            </div>
            <div className="col-md-6">
              {isFetching ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <Tooltip
                  title={!clientId ? 'Por favor seleccione una plaza' : ''}
                  arrow
                >
                  <Autocomplete
                    disabled={!clientId || isLoading}
                    options={sites}
                    value={sites.find((s) => s.id === siteId) || null}
                    getOptionLabel={(s) => s.site_name || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(_, newValue) =>
                      setSiteId(newValue ? newValue.id : null)
                    }
                    renderInput={(params) => (
                      <TextField
                        required
                        {...params}
                        label="Sucursal"
                        placeholder="Sucursal..."
                      />
                    )}
                  />
                </Tooltip>
              )}
            </div>
          </div>

          <br />

          <div className="row">
            <div className="col-md-6">
              {isFetching ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <Autocomplete
                  disabled={isLoading}
                  options={locations}
                  value={locations.find((l) => l.id === locationId) || null}
                  getOptionLabel={(l) => l.name || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_, newValue) =>
                    setLocationId(newValue ? newValue.id : null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ubicación"
                      placeholder="Ubicación..."
                    />
                  )}
                />
              )}
            </div>
          </div>

          <br />

          <div className="row">
            <div className="col-md-12">
              <TextField
                disabled={isLoading}
                fullWidth
                label={notesString}
                placeholder={`${notesString}...`}
                type="text"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="contained"
          form="new-asset-form"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size="2rem" />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalA;
